import { Flex, Grid, Modal, Text } from "@mantine/core";
import { Fragment, useMemo } from "react";

import ReviewsAndAudits from "./ReviewsAndAudits";
import {
  VesselAudits,
  VesselEntries,
  VesselParticulars,
  VesselReviews,
  VesselSubscriptions,
} from "./YachtDetails.enum";
import { type YachtDetailsTypes } from "./YachtDetails.types";

interface Props {
  params: YachtDetailsTypes;
  opened?: boolean;
  close?: () => void;
  type: string;
  contentOnly?: boolean;
  columns?: number;
}

const YachtDetailsGridRow = ({
  label,
  value,
  colSpan,
}: {
  label: any;
  value: string;
  colSpan: number;
}) => (
  <Grid.Col span={colSpan} key={label}>
    <Grid>
      <Grid.Col span={7} style={{ paddingBottom: 4 }}>
        <Text fw={300} c="text">
          {label}:
        </Text>
      </Grid.Col>
      <Grid.Col span={5} style={{ paddingBottom: 4 }}>
        <Text fw={600}>{value}</Text>
      </Grid.Col>
    </Grid>
  </Grid.Col>
);

interface KeyValuePair {
  key: string;
  value: string;
}

const matchedEnums = {
  "Vessel Particulars": VesselParticulars,
  Entries: VesselEntries,
  Subscriptions: VesselSubscriptions,
  Reviews: VesselReviews,
  Audits: VesselAudits,
};

const YachtDetails = ({
  params,
  opened,
  close,
  type,
  contentOnly,
  columns,
}: Props) => {
  const segregatedParams = useMemo(() => {
    if (!params) return [];
    const tempParams: KeyValuePair[] = [];
    Object.entries(params).forEach(([key, value]) => {
      const matchedEnum = matchedEnums[type as keyof typeof matchedEnums];
      if (matchedEnum && key in matchedEnum) {
        tempParams.push({
          key: matchedEnum[key as keyof typeof matchedEnum],
          value,
        });
      }
    });
    return tempParams;
  }, [params, type]);

  const getContent = () =>
    params ? (
      <Fragment>
        {type === "Cabins" && (
          <Flex direction="column" mb={20}>
            {params?.cabins?.map((value, index) => (
              <Flex key={index} gap={5}>
                <Text opacity={0.5}>{index + 1}º</Text>
                <Text>{value}</Text>
              </Flex>
            ))}
          </Flex>
        )}

        <Grid maw={type === "Reviews" || type === "Audits" ? 500 : undefined}>
          {segregatedParams?.map(({ key, value }) => (
            <YachtDetailsGridRow
              key={key}
              label={key}
              value={value}
              colSpan={columns ? 12 / columns : 12}
            />
          ))}
        </Grid>
        {type === "Reviews" && <ReviewsAndAudits />}
        {type === "Audits" && <ReviewsAndAudits isAudit />}
      </Fragment>
    ) : (
      <Text>No data</Text>
    );

  return contentOnly
    ? getContent()
    : close && (
        <Modal
          opened={Boolean(opened)}
          onClose={close}
          title={type}
          size={
            type === "Cabins"
              ? "md"
              : type === "Reviews" || type === "Audits"
                ? "1200px"
                : "lg"
          }>
          {getContent()}
        </Modal>
      );
};

export default YachtDetails;
